/**
 * The three things Probability's form needs that a stylesheet cannot express.
 * Everything else — sizes, colours, the grid, the button — is in donate.scss;
 * this covers only what CSS provably cannot reach.
 *
 * 1. THE FIELD NAME GOES INSIDE THE BOX. Figma draws it as the placeholder
 *    (482:131 "First Name" sits at x456 inside the field at x431; 482:136,
 *    482:138, 482:140 likewise). The program config does the opposite: the name
 *    is the label above and the placeholder is an example value — "John", "Doe",
 *    "your@email.com", "(123) 456-7890", "New York", "12345". Placeholder text is
 *    content, and `::placeholder` can recolour it but cannot change what it says.
 *
 *    So the label is copied into the field's own placeholder (or, for a <select>,
 *    into its empty prompt option) and flagged `data-vv-adopted` for the
 *    stylesheet to clip. The label element STAYS in the DOM: the `<label>`
 *    wrapping the input is what names the field for screen readers.
 *
 * 2. BLANK SLOTS MUST NOT TAKE UP A ROW. Their template always emits a title, a
 *    description, a captcha holder and two copy slots, whether or not the program
 *    config fills them. Blank they are 0px tall, but they are still grid items,
 *    so each one eats a row-gap — 145px between the last field and the button
 *    instead of the design's 38px.
 *
 *    `:empty` cannot express this. Lit leaves marker comments AND whitespace text
 *    nodes inside its bindings; Chrome's `:empty` tolerates the comments but not
 *    the whitespace, so it matches some of these elements and not others. Blank
 *    is therefore decided here and published as `data-vv-blank`.
 *
 * 3. THE ZIP CODE MUST REFUSE LETTERS AND STOP AT FIVE DIGITS, as it is typed.
 *    The program config already carries `regexp: ^[0-9]+$` for this field, but a
 *    regexp there runs at VALIDATION time: the visitor can still type "abc",
 *    reach the button, and only then be told. There is no length rule at all —
 *    `limit_field` is false and `limit_count` is 500.
 *
 *    Neither is reachable from CSS, and neither can be expressed with the
 *    attributes alone: `maxlength` caps the length but accepts any character,
 *    and `inputmode="numeric"` only hints at a keyboard on touch devices — a
 *    hardware keyboard ignores it completely.
 *
 * THE DURABLE FIX FOR (1) IS THE PROGRAM CONFIG: set each field's placeholder to
 * its name and clear its label, and the design comes out right with no code at
 * all. Delete that half the day it lands.
 *
 * THE DURABLE FIX FOR THE LENGTH HALF OF (3) IS ALSO THE PROGRAM CONFIG: set
 * `limit_field: true` and `limit_count: 5` on the zipcode field. That would make
 * the cap survive on their side too, where the entry is actually stored — this
 * module only guards the browser.
 *
 * Both jobs have to survive their re-renders — the whole subtree is replaced when
 * the form switches to its thank-you or error state — hence the observer.
 */

/** Slots their template emits unconditionally, filled or not. */
const OPTIONAL_SLOTS = [
  "#loader-form-input > h1",
  "#loader-form-input > .description",
  "#recaptcha",
  ".text-description-2",
  ".text-disclaimer",
].join(",");

/**
 * The label span carries a `<span class="asterisk">` for required fields, plus
 * Lit's marker comments. This works on a clone and reads `textContent` off it:
 * `Element.textContent` skips comment nodes, whereas walking `childNodes` and
 * reading each one picks the markers up as text.
 *
 * The label arrives from the program config as raw HTML and may contain markup,
 * so whitespace is collapsed to keep a multi-line one usable as a placeholder.
 */
function labelTextOf(span: Element): string {
  const clone = span.cloneNode(true) as Element;
  for (const asterisk of clone.querySelectorAll(".asterisk")) asterisk.remove();
  return (clone.textContent ?? "").replace(/\s+/g, " ").trim();
}

function adaptFields(root: ParentNode): void {
  for (const field of root.querySelectorAll<HTMLElement>(".style-label")) {
    const span = field.querySelector<HTMLElement>('[id^="label-"]');
    const control = field.querySelector<HTMLInputElement | HTMLSelectElement>(".style-input");
    if (!span || !control) continue;

    const text = labelTextOf(span);
    if (!text) continue;

    if (control instanceof HTMLSelectElement) {
      // Their prompt is the first option, not a placeholder attribute. Guarded so
      // writing it cannot re-trigger the observer indefinitely.
      const prompt = control.querySelector('option[value=""]');
      if (prompt && prompt.textContent !== text) prompt.textContent = text;
    } else if (control.placeholder !== text) {
      control.placeholder = text;
    }

    span.dataset.vvAdopted = "";
  }
}

/** US ZIP without the +4. The program config's own regexp allows any length. */
const ZIP_LENGTH = 5;

/** Trims to digits and to length — the single definition of a valid value. */
function asZip(value: string): string {
  return value.replace(/\D/g, "").slice(0, ZIP_LENGTH);
}

/**
 * Stops letters ever reaching the zip field, rather than rejecting them later.
 *
 * Two layers, because neither covers the other:
 *
 * - `beforeinput` refuses the keystroke outright, so the character never appears
 *   and the caret never moves. This is what makes it feel like the field simply
 *   will not take letters.
 * - `input` is the net behind it, for the paths that produce no cancelable
 *   `beforeinput`: autofill, drag-and-drop, and some IME commits. Here the value
 *   is already wrong, so it is rewritten and an `input` event re-dispatched —
 *   Probability read their value from that event, so skipping it would leave
 *   their copy holding the unsanitised text.
 *
 * The re-dispatch cannot loop: the second pass sees an already-clean value and
 * returns before touching anything.
 */
function constrainZip(root: ParentNode): void {
  const input = root.querySelector<HTMLInputElement>("#zipcode");
  if (!input || input.dataset.vvZip !== undefined) return;
  input.dataset.vvZip = "";

  input.inputMode = "numeric";
  input.autocomplete = "postal-code";
  input.maxLength = ZIP_LENGTH;

  input.addEventListener("beforeinput", (event) => {
    const data = (event as InputEvent).data;
    if (data && /\D/.test(data)) event.preventDefault();
  });

  input.addEventListener("input", () => {
    const clean = asZip(input.value);
    if (clean === input.value) return;
    input.value = clean;
    input.dispatchEvent(new Event("input", { bubbles: true }));
  });
}

function flagBlankSlots(root: ParentNode): void {
  for (const slot of root.querySelectorAll<HTMLElement>(OPTIONAL_SLOTS)) {
    const blank = !slot.textContent?.trim() && slot.children.length === 0;
    if (blank) slot.dataset.vvBlank = "";
    else delete slot.dataset.vvBlank;
  }
}

/**
 * Applies both adaptations inside `root` and keeps applying them as Probability
 * re-render. Returns a teardown function. Safe to call before their script has
 * loaded — the observer picks the form up whenever it appears.
 */
export function adaptProbabilityForm(root: HTMLElement): () => void {
  const run = (): void => {
    adaptFields(root);
    constrainZip(root);
    flagBlankSlots(root);
  };

  if (typeof MutationObserver === "undefined") {
    run();
    return () => {};
  }

  // childList only: the writes above are almost all attributes, so observing
  // attributes would loop. The one childList write (the select prompt) is guarded
  // by an equality check, so the re-entry settles after a single extra pass.
  const observer = new MutationObserver(run);
  observer.observe(root, { childList: true, subtree: true });
  run();

  return () => observer.disconnect();
}
